var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Prop, Vue } from 'vue-property-decorator';
var numberFormat = null;
/** Numbers with thousands separators in the browser's locale (324 922 / 324,922); anything else as it is. */
function formatValue(value) {
    if (typeof value !== 'number' || !isFinite(value))
        return value;
    if (!numberFormat) {
        try {
            numberFormat = new Intl.NumberFormat(navigator.language || undefined, { maximumFractionDigits: 2 });
        }
        catch (e) {
            numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
        }
    }
    return numberFormat.format(value);
}
var compactFormat = null;
/** The value axis: short forms for large numbers (100 t. / 100K), so its ticks fit. */
function formatAxisValue(value) {
    if (typeof value !== 'number' || !isFinite(value) || Math.abs(value) < 10000)
        return formatValue(value);
    if (!compactFormat) {
        try {
            compactFormat = new Intl.NumberFormat(navigator.language || undefined, { notation: 'compact', maximumFractionDigits: 1 });
        }
        catch (e) {
            return formatValue(value);
        }
    }
    return compactFormat.format(value);
}
/**
 * Thousands separators on the value axis, the value labels and the tooltips. ApexCharts takes
 * formatters only as functions, which the JSON options (the model's ```chart block, or deep
 * mode's render_chart artifact) cannot carry. Only what the options leave unset is filled in;
 * text values pass through, so category labels are never touched.
 */
function applyNumberFormat(options) {
    var type = options.chart && options.chart.type;
    options.tooltip = options.tooltip || {};
    options.tooltip.y = options.tooltip.y || {};
    if (!options.tooltip.y.formatter)
        options.tooltip.y.formatter = formatValue;
    if (type === 'pie' || type === 'donut')
        return; // their labels are percentages
    options.dataLabels = options.dataLabels || {};
    if (!options.dataLabels.formatter)
        options.dataLabels.formatter = formatValue;
    // Horizontal bars carry their values on the x-axis.
    var horizontal = options.plotOptions && options.plotOptions.bar && options.plotOptions.bar.horizontal;
    var valueAxis = horizontal ? 'xaxis' : 'yaxis';
    if (Array.isArray(options[valueAxis]))
        return;
    options[valueAxis] = options[valueAxis] || {};
    options[valueAxis].labels = options[valueAxis].labels || {};
    if (!options[valueAxis].labels.formatter)
        options[valueAxis].labels.formatter = formatAxisValue;
}
/**
 * Horizontal bars with a value label after each: stretch the value axis just enough that the
 * longest bar's label fits after it. ApexCharts keeps labels inside the plot area, and how much
 * room a label needs depends on the chart's width on screen, which only the page knows.
 */
function fitBarLabels(options, width) {
    var bar = options.plotOptions && options.plotOptions.bar;
    if (!bar || !bar.horizontal || !options.dataLabels || !options.dataLabels.enabled)
        return;
    if (!options.xaxis || options.xaxis.max !== undefined || !width)
        return;
    var values = [];
    (options.series || []).forEach(function (s) {
        (s.data || []).forEach(function (v) {
            if (typeof v === 'number' && isFinite(v))
                values.push(v);
        });
    });
    if (!values.length)
        return;
    var largest = Math.max.apply(Math, values);
    if (largest <= 0)
        return;
    var longest = (options.xaxis.categories || []).reduce(function (n, c) { return Math.max(n, String(c).length); }, 1);
    var maxLabel = (options.yaxis && options.yaxis.labels && options.yaxis.labels.maxWidth) || 160;
    var plot = Math.max(width - Math.min(maxLabel, 7 * longest) - 40, 60);
    var labelPx = Math.min(7.5 * String(formatValue(largest)).length + (options.dataLabels.offsetX || 0) + 10, plot * 0.7);
    options.xaxis.max = (largest * plot) / (plot - labelPx);
}
var ChartBlock = /** @class */ (function (_super) {
    __extends(ChartBlock, _super);
    function ChartBlock() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.error = null;
        _this.chart = null;
        return _this;
    }
    ChartBlock.prototype.mounted = function () {
        this.build();
    };
    ChartBlock.prototype.beforeDestroy = function () {
        if (this.chart) {
            try {
                this.chart.destroy();
            }
            catch (e) {
                /* ignore */
            }
            this.chart = null;
        }
    };
    ChartBlock.prototype.build = function () {
        var _this = this;
        var options;
        try {
            options = JSON.parse(this.spec);
        }
        catch (e) {
            this.error = this.errorText;
            return;
        }
        if (!options || typeof options !== 'object' || !options.series || !options.chart || !options.chart.type) {
            this.error = this.errorText;
            return;
        }
        // Fill in only what the model left out — a chart with no explicit size
        // otherwise renders at ApexCharts' own default, which doesn't fit the bubble.
        if (options.chart.width === undefined)
            options.chart.width = '100%';
        if (options.chart.height === undefined)
            options.chart.height = 300;
        applyNumberFormat(options);
        this.$nextTick(function () {
            var el = _this.$refs.container;
            if (!el)
                return;
            fitBarLabels(options, el.clientWidth);
            try {
                _this.chart = new ApexCharts(el, options);
                _this.chart.render();
            }
            catch (e) {
                _this.chart = null;
                _this.error = _this.errorText;
            }
        });
    };
    __decorate([
        Prop({ type: String, required: true })
    ], ChartBlock.prototype, "spec", void 0);
    __decorate([
        Prop({ type: String, required: true })
    ], ChartBlock.prototype, "errorText", void 0);
    __decorate([
        Prop({ type: Boolean, default: false })
    ], ChartBlock.prototype, "wide", void 0);
    ChartBlock = __decorate([
        Component
    ], ChartBlock);
    return ChartBlock;
}(Vue));
export default ChartBlock;
